import {
  aiMessageOpenStates,
  aiReplyEnvelopeSchema,
  systemNoticeEnvelopeSchema,
} from "@lafayette/schemas";

import type {
  MessageAuthorKind,
  MessageStatus,
  ThreadAttachment,
  ThreadMessage,
} from "./thread-client";

// Stream message to workspace message (CHT-732). Pure: no SDK type enters here. The adapter turns an SDK message into a
// `RawMessage` (plain data) and this module does the rest, so every rule below has a unit test without a browser or a socket.

export type AiReplyEnvelope = ReturnType<typeof aiReplyEnvelopeSchema.parse>;

/** The few fields of a Stream message that the workspace reads. All optional: Stream omits what it does not have. */
export type RawMessage = Readonly<{
  id: string;
  text?: string | null | undefined;
  type?: string | undefined;
  status?: string | undefined;
  created_at?: string | Date | undefined;
  updated_at?: string | Date | undefined;
  deleted_at?: string | Date | null | undefined;
  user?: Readonly<{ id: string; name?: string | undefined }> | null | undefined;
  attachments?:
    | readonly Readonly<{
        type?: string | undefined;
        title?: string | undefined;
        name?: string | undefined;
        image_url?: string | undefined;
        asset_url?: string | undefined;
        thumb_url?: string | undefined;
        mime_type?: string | undefined;
        file_size?: number | undefined;
      }>[]
    | undefined;
  /** The Lafayette envelope (`lf`), unparsed. */
  lf?: unknown;
}>;

const USER_ID =
  /^[a-z][a-z0-9]{1,7}-(?:(cust|staff)-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|lafayette-(ai|system|copilot))$/u;

/** Who wrote a message, from the Stream user id (the same derivation the server uses; never from message data). */
export function authorKindOf(userId: string | undefined): MessageAuthorKind {
  const match = userId === undefined ? null : USER_ID.exec(userId);
  if (match === null) return "UNKNOWN";
  if (match[1] === "cust") return "CUSTOMER";
  if (match[1] === "staff") return "CONCIERGE";
  if (match[2] === "ai") return "LAFAYETTE_AI";
  if (match[2] === "system") return "SYSTEM";
  return "COPILOT";
}

const toIso = (value: string | Date | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

/** A client treats THINKING or STREAMING older than 3 minutes without an update as INTERRUPTED (contract section 9). */
export const AI_OPEN_STATE_TIMEOUT_MS = 3 * 60 * 1000;

/**
 * The hosts that Stream serves uploaded files from. A customer client holds its own Stream token and can post an attachment with ANY
 * address, so only these hosts are loaded or linked by the console on their own. The admin's Content-Security-Policy
 * (`public/_headers`) allows the same hosts in `img-src`.
 */
export const STREAM_CDN_HOST_SUFFIX = "stream-io-cdn.com";

/** The host of an https address that is not a Stream CDN host, or `null` when it is one (or is not an https address at all). */
function hostOf(url: string): { host: string; trusted: boolean } | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:" || parsed.username !== "" || parsed.password !== "") return null;
  const host = parsed.hostname.toLowerCase();
  const trusted = host === STREAM_CDN_HOST_SUFFIX || host.endsWith(`.${STREAM_CDN_HOST_SUFFIX}`);
  return { host, trusted };
}

function attachmentsOf(raw: RawMessage): readonly ThreadAttachment[] {
  const list: ThreadAttachment[] = [];
  for (const attachment of raw.attachments ?? []) {
    const kind =
      attachment.type === "image" || attachment.image_url !== undefined ? "IMAGE" : "FILE";
    const url = attachment.image_url ?? attachment.asset_url ?? attachment.thumb_url ?? null;
    // Only https addresses are drawn: anything else (javascript:, data:, plain http) is dropped, so a hostile attachment cannot become
    // a link. An https address on a host that is not Stream's is kept apart: the console shows the host and never loads it itself.
    const where = url === null ? null : hostOf(url);
    const trusted = where?.trusted === true;
    list.push({
      kind,
      name: (attachment.title ?? attachment.name ?? (kind === "IMAGE" ? "Image" : "File")).slice(
        0,
        200,
      ),
      url: trusted ? url : null,
      external: where !== null && !where.trusted && url !== null ? { host: where.host, url } : null,
      mimeType: attachment.mime_type ?? null,
      sizeBytes: attachment.file_size ?? null,
    });
  }
  return list;
}

function statusOf(raw: RawMessage): MessageStatus {
  if (raw.status === "sending") return "sending";
  if (raw.status === "failed") return "failed";
  return "sent";
}

/**
 * One message for the list. An envelope that does not parse is ignored and the text shows (contract section 3.5). An open AI state
 * (THINKING, STREAMING) that has not been updated for 3 minutes reads as INTERRUPTED.
 */
export function toThreadMessage(
  raw: RawMessage,
  context: Readonly<{ ownUserId: string; now: Date }>,
): ThreadMessage {
  const authorKind = authorKindOf(raw.user?.id);
  const createdAt = toIso(raw.created_at) ?? context.now.toISOString();
  const deleted = raw.type === "deleted" || toIso(raw.deleted_at) !== null;

  let ai: AiReplyEnvelope | null = null;
  let systemNotice: ThreadMessage["systemNotice"] = null;
  const envelope = raw.lf;
  if (!deleted && envelope !== undefined && envelope !== null) {
    if (authorKind === "LAFAYETTE_AI") {
      const parsed = aiReplyEnvelopeSchema.safeParse(envelope);
      if (parsed.success) {
        ai = parsed.data;
        const updated = toIso(raw.updated_at) ?? createdAt;
        const stale =
          (aiMessageOpenStates as readonly string[]).includes(ai.state) &&
          context.now.getTime() - new Date(updated).getTime() > AI_OPEN_STATE_TIMEOUT_MS;
        if (stale) ai = { ...ai, state: "INTERRUPTED", outcome: ai.outcome };
      }
    } else if (authorKind === "SYSTEM") {
      const parsed = systemNoticeEnvelopeSchema.safeParse(envelope);
      if (parsed.success) systemNotice = parsed.data.code;
    }
  }

  return {
    id: raw.id,
    authorKind,
    authorName: authorNameOf(authorKind, raw.user?.name),
    own: raw.user?.id === context.ownUserId,
    text: deleted ? "" : (raw.text ?? ""),
    createdAt,
    status: statusOf(raw),
    attachments: deleted ? [] : attachmentsOf(raw),
    ai,
    systemNotice,
    deleted,
  };
}

/**
 * The name above a message. Only a STAFF member's Stream name is shown: a customer can rename their own Stream user (the role keeps
 * `update-user-owner`), for example to "Supervisor" or "Lafayette AI", so the name of every other author kind is the fixed label of
 * the kind, which comes from the user id and not from data the author controls.
 */
function authorNameOf(kind: MessageAuthorKind, streamName: string | undefined): string {
  return kind === "CONCIERGE" ? streamName?.slice(0, 80) || authorLabel(kind) : authorLabel(kind);
}

function authorLabel(kind: MessageAuthorKind): string {
  switch (kind) {
    case "CUSTOMER":
      return "Customer";
    case "LAFAYETTE_AI":
    case "SYSTEM":
      return "Lafayette";
    case "CONCIERGE":
      return "Concierge";
    case "COPILOT":
      return "Copilot";
    default:
      return "Unknown";
  }
}
